import React, { ReactNode } from 'react'
import { ConfigProvider } from 'antd'

import { useIdentity } from '@/hooks/auth/useIdentity'
import { isProjectHubRole } from '@/utils/projectHubRole'
import type { Account } from 'types'

import { hubTheme } from './hubTheme'

/**
 * Renders the page body at hub scale for the two project-hub roles.
 *
 * The hub's own styled-components carry their enlargement in their `px()`
 * dimensions and need no provider; this one covers the Ant Design widgets
 * rendered inside them, whose sizes come from theme tokens (see `hubTheme.ts`).
 *
 * Mounted around the routed content only, so the sider and top header — shared
 * chrome, not a hub surface — keep the base scale. The nested `ConfigProvider`
 * merges onto the app-wide theme rather than replacing it, and AntD carries its
 * theme context into portals, so modals, dropdowns and tooltips opened from the
 * hub are scaled too even though they mount outside this subtree.
 *
 * Non-hub roles get no provider at all, not an identity one, so nothing about
 * the architect / back-office render path changes.
 */
export const HubScaleProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { data: identity } = useIdentity<Account>()

  if (!isProjectHubRole(identity?.account_type)) {
    return <>{children}</>
  }

  return <ConfigProvider theme={hubTheme}>{children}</ConfigProvider>
}
